/**
 * Synthetic team used only by company-level Repository Ownership requests.
 * When it is present in the hierarchy, Watchtower assigns every non-archived
 * repository not owned by a requested team to it.
 */
export const INVALID_SUP_ORG_TEAM_ID = 'SUP_INVALID';

export const INVALID_SUP_ORG_TEAM_NAME =
  'Invalid or Detached Supervisory Organisations';

/**
 * The only scorecard category that understands SUP_INVALID.
 */
export const REPOSITORY_OWNERSHIP_CATEGORY_URL =
  '/v1/repos/scorecard/repository-ownership';

type HierarchyNode = {
  team_id: string;
  children: HierarchyNode[];
};

export const isInvalidSupOrgTeamId = (teamId?: string | null): boolean =>
  teamId?.trim().toUpperCase() === INVALID_SUP_ORG_TEAM_ID;

/**
 * Appends SUP_INVALID as the last child of the root team. The node carries only
 * team_id and children, as Watchtower rejects unknown keys on hierarchy nodes.
 */
export const withInvalidSupOrgTeam = <T extends HierarchyNode>(root: T): T =>
  ({
    ...root,
    children: [
      ...root.children,
      { team_id: INVALID_SUP_ORG_TEAM_ID, children: [] },
    ],
  } as T);

const removeInvalidSupOrgTeam = (nodes: HierarchyNode[]): HierarchyNode[] =>
  nodes
    .filter(node => !isInvalidSupOrgTeamId(node.team_id))
    .map(node => ({
      ...node,
      children: removeInvalidSupOrgTeam(node.children ?? []),
    }));

/**
 * Returns the scorecard request body with SUP_INVALID removed from its
 * team_ids hierarchy. Bodies without a team_ids array are returned unchanged.
 */
export const withoutInvalidSupOrgTeam = <T>(body: T): T => {
  const teamIds = (body as { team_ids?: unknown } | undefined)?.team_ids;
  return Array.isArray(teamIds)
    ? { ...body, team_ids: removeInvalidSupOrgTeam(teamIds) }
    : body;
};
